// ---------------------------------------------------------------------------
// Data
// ---------------------------------------------------------------------------

export interface ScatterPlotPoint {
  readonly x: number;
  readonly y: number;
}

// ---------------------------------------------------------------------------
// Params
// ---------------------------------------------------------------------------

export interface ScatterPlotParams {
  readonly points: ScatterPlotPoint[];
  readonly xLabel: string;
  readonly yLabel: string;
}

// ---------------------------------------------------------------------------
// Regression type
// ---------------------------------------------------------------------------

export type RegressionType =
  | 'linear'
  | 'quadratic'
  | 'cubic'
  | 'exponential'
  | 'power'
  | 'logarithmic'
  | 'logistic'
  | 'sine';

// ---------------------------------------------------------------------------
// Display
// ---------------------------------------------------------------------------

export interface ScatterPlotDisplayConfig {
  readonly showRegressionLine?: boolean;
  /** Type of regression curve. Defaults to 'linear'. */
  readonly regressionType?: RegressionType;
  readonly showCorrelation?: boolean;
  readonly showResiduals?: boolean;
  readonly dataZoomX?: boolean;
  readonly dataZoomY?: boolean;
  readonly dotColor?: string;
  readonly dotSize?: number;
  readonly regressionLineColor?: string;
  readonly residualColor?: string;
  /** When set, auto-computes y from the regression line and shows the prediction on the chart. */
  readonly predictX?: number;
  /** When set, shows the mean point (x̄, ȳ) on the chart. */
  readonly showMeanPoint?: boolean;
  /** When set, shows static slope triangle and intercept annotations on the regression line. */
  readonly explainRegressionLine?: boolean;
  /** When set, shows the x on y regression line (inverse regression). */
  readonly showInverseRegressionLine?: boolean;
  /** Color for the inverse regression line. Default: '#ef4444' (red-500). */
  readonly inverseRegressionLineColor?: string;
  /** When true, shows alphabetic labels (A, B, C, …) above each scatter point. */
  readonly showPointLabels?: boolean;
  /** Color for point labels. Defaults to dotColor. */
  readonly pointLabelColor?: string;
}

export const DEFAULT_SCATTER_PLOT_DISPLAY: ScatterPlotDisplayConfig = {
  showRegressionLine: true,
  showCorrelation: true,
  showResiduals: false,
  dotColor: '#0ea5e9', // sky-500
  dotSize: 10,
  regressionLineColor: '#8b5cf6', // violet-500
  residualColor: '#ef4444', // red-500
};

// ---------------------------------------------------------------------------
// Prediction
// ---------------------------------------------------------------------------

export interface ScatterPlotPrediction {
  readonly x: number;
  readonly y: number;
}
